import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { toast } from "sonner";
import {
  Stethoscope, ArrowLeft, RefreshCw, Plus, Pencil, X, Loader2, Save, Search, CalendarDays, Users, Wallet,
  BarChart3, Settings, Check, Ban, UserX, Receipt, Trash2, Download, ChevronLeft, ChevronRight, FileText,
  Fingerprint, Printer, ChevronDown, Upload,
} from "lucide-react";
import { capturarDesdeServicioLocal, compararSecuGen, UMBRAL_COINCIDENCIA_SECUGEN } from "../lib/huellaLector";
import { imprimirDocumentoClinico } from "../lib/clinicaDocumentos";
import DateInput from "../components/ui/DateInput";
import FacturaFormModal, { ClienteSearchSelect } from "../components/FacturaFormModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BTN_PRI = "flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 text-white rounded-lg transition-all disabled:opacity-50";
const BTN_SEC = "flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg border border-white/10 disabled:opacity-50";
const BTN_ICO = "p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 disabled:opacity-40";
const INPUT = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const LABEL = "block text-xs text-slate-400 mb-1";
const CARD = "bg-arandu-dark-light border border-white/10 rounded-xl";
const TH = "text-left text-xs font-medium text-slate-400 px-3 py-2";
const TD = "px-3 py-2 text-sm text-slate-200";

const hoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const sumarDias = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const fmtFecha = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : iso || "");
const fmtHora = (iso) => (iso || "").slice(11, 16);
const fmtMonto = (n, moneda = "PYG") => {
  const v = Number(n || 0);
  const s = moneda === "PYG" ? Math.round(v).toLocaleString("es-PY") : v.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${moneda === "PYG" ? "₲" : moneda === "USD" ? "US$" : moneda === "BRL" ? "R$" : moneda} ${s}`;
};

const ESTADO_STYLE = {
  pendiente: "bg-amber-500/15 text-amber-400",
  confirmado: "bg-sky-500/15 text-sky-400",
  atendido: "bg-emerald-500/15 text-emerald-400",
  cancelado: "bg-red-500/15 text-red-400",
  ausente: "bg-slate-500/20 text-slate-400",
};
const Badge = ({ estado }) => <span className={`text-xs px-2 py-0.5 rounded-full ${ESTADO_STYLE[estado] || ""}`}>{estado}</span>;

function Modal({ titulo, onClose, children, ancho = "max-w-xl" }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className={`bg-arandu-dark-light border border-white/10 rounded-2xl p-6 w-full ${ancho} max-h-[92vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-bold text-white">{titulo}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function ClinicaPage() {
  const { token, hasPermission, activeEmpresaPropia, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const logo = activeEmpresaPropia?.slug || user?.logos_asignados?.[0] || "";
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }), [token]);
  const P = (p) => hasPermission(p);

  const [tab, setTab] = useState("agenda");
  const [cfgTab, setCfgTab] = useState("profesionales");
  const [profesionales, setProfesionales] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [comisiones, setComisiones] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [planCuentas, setPlanCuentas] = useState([]);
  const [ctx, setCtx] = useState({ es_admin: false, profesional_id: null, admins: [], usuarios: [] });

  const qs = useCallback((extra = {}) => new URLSearchParams({ logo_tipo: logo, ...extra }).toString(), [logo]);
  const api = useCallback(async (path, { method = "GET", body, query } = {}) => {
    const sep = path.includes("?") ? "&" : "?";
    const r = await fetch(`${API}${path}${sep}${qs(query || {})}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Error en la solicitud");
    return d;
  }, [headers, qs]);

  const cargarBase = useCallback(async () => {
    if (!logo) return;
    const get = async (path) => { try { return await api(path); } catch { return []; } };
    const [pr, sv, pc] = await Promise.all([
      get("/admin/clinica/profesionales?incluir_inactivos=true"),
      get("/admin/clinica/servicios?incluir_inactivos=true"),
      P("clinica_pacientes.ver") ? get("/admin/clinica/pacientes?incluir_inactivos=true") : [],
    ]);
    setProfesionales(pr); setServicios(sv); setPacientes(pc);
    try { const c = await api("/admin/clinica/config"); setCtx({ usuarios: [], admins: [], ...c }); } catch { /* sin contexto: se comporta como antes */ }
    if (P("clinica_configuracion.ver")) setComisiones(await get("/admin/clinica/comisiones"));
    // eslint-disable-next-line
  }, [api, logo]);

  const cargarClientes = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/entidades?es_cliente=true&activo=true&logo_tipo=${logo}`, { headers });
      const d = r.ok ? await r.json() : [];
      setClientes((Array.isArray(d) ? d : []).map(c => ({ ...c, empresa_id: c.id, nombre: c.nombre || c.razon_social || "", razon_social: c.razon_social || c.nombre || "", logo_tipo: c.logo_tipo || logo })));
    } catch { setClientes([]); }
  }, [headers, logo]);

  useEffect(() => { cargarBase(); }, [cargarBase]);
  useEffect(() => {
    cargarClientes();
    Promise.all([
      fetch(`${API}/admin/cuentas-bancarias?logo_tipo=${logo}`, { headers }),
      fetch(`${API}/admin/plan-cuentas?logo_tipo=${logo}`, { headers }),
    ]).then(async ([a, b]) => { if (a.ok) setCuentas(await a.json()); if (b.ok) setPlanCuentas(await b.json()); }).catch(() => {});
    // eslint-disable-next-line
  }, [logo, token]);

  const TABS = [
    ["agenda", "Agenda", CalendarDays, P("clinica_agenda.ver")],
    ["pacientes", "Pacientes", Users, P("clinica_pacientes.ver")],
    ["liquidaciones", "Comisiones", Wallet, P("clinica_liquidaciones.ver")],
    ["reportes", "Reportes", BarChart3, P("clinica_reportes.ver")],
    ["config", "Configuración", Settings, P("clinica_configuracion.ver")],
  ].filter(t => t[3]);

  const props = { ctx, recargarCtx: cargarBase, api, P, profesionales, servicios, comisiones, pacientes, clientes, cuentas, planCuentas, token, logo, recargar: cargarBase, recargarClientes: cargarClientes, activeEmpresaPropia, hasPermission };

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => navigate(-1)} className={BTN_ICO}><ArrowLeft className="w-4 h-4" /></button>
          <Stethoscope className="w-6 h-6 text-teal-400" />
          <h1 className="font-heading text-xl font-bold">Clínica / Fisioterapia</h1>
          <button onClick={() => { cargarBase(); cargarClientes(); }} className={`${BTN_ICO} ml-auto`} title="Actualizar"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2 mb-5 flex-wrap">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg border transition-all ${tab === k ? "bg-teal-500/20 border-teal-500/40 text-teal-300" : "bg-white/5 border-white/10 text-slate-400 hover:text-white"}`}>
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>
        {tab === "agenda" && <Agenda {...props} />}
        {tab === "pacientes" && <Pacientes {...props} />}
        {tab === "liquidaciones" && <Liquidaciones {...props} />}
        {tab === "reportes" && <Reportes {...props} />}
        {tab === "config" && (
          <div>
            <div className="flex gap-2 mb-4">
              {[["profesionales", "Profesionales"], ["servicios", "Servicios"], ["comisiones", "Comisiones"], ...(ctx.es_admin ? [["administradores", "Administradores"]] : [])].map(([k, l]) => (
                <button key={k} onClick={() => setCfgTab(k)} className={`px-3 py-1.5 text-sm rounded-lg border ${cfgTab === k ? "bg-white/10 border-white/20 text-white" : "bg-transparent border-white/10 text-slate-400"}`}>{l}</button>
              ))}
            </div>
            {cfgTab === "profesionales" && <CfgProfesionales {...props} />}
            {cfgTab === "servicios" && <CfgServicios {...props} />}
            {cfgTab === "comisiones" && <CfgComisiones {...props} />}
            {cfgTab === "administradores" && ctx.es_admin && <CfgAdministradores {...props} />}
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════ AGENDA ════════════════════════════════ */

const ESTADOS_LISTA = ["pendiente", "confirmado", "atendido", "cancelado", "ausente"];

function Agenda({ ctx, api, P, profesionales, servicios, pacientes, clientes, cuentas, planCuentas, token, logo, recargar, recargarClientes, activeEmpresaPropia, hasPermission }) {
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());
  const [fProf, setFProf] = useState("");
  const [fEstados, setFEstados] = useState([]);          // checkboxes: vacío = todos
  const [estadoAbierto, setEstadoAbierto] = useState(false);
  const [sel, setSel] = useState({});                    // turnos atendidos marcados para facturar
  const [asistir, setAsistir] = useState(null);
  const [q, setQ] = useState("");
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [atender, setAtender] = useState(null);
  const [facturar, setFacturar] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const query = { desde, hasta };
      if (fProf) query.profesional_id = fProf;
      if (fEstados.length) query.estado = fEstados.join(",");
      if (q) query.q = q;
      setTurnos(await api("/admin/clinica/turnos", { query }));
      setSel({});
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [api, desde, hasta, fProf, fEstados, q]);
  useEffect(() => { cargar(); }, [cargar]);

  const cambiarEstado = async (t, estado, extra = {}) => {
    try {
      await api(`/admin/clinica/turnos/${t.id}/estado`, { method: "POST", body: { estado, ...extra } });
      toast.success(`Turno ${estado}`); cargar();
    } catch (e) { toast.error(e.message); }
  };

  const dia = (n) => { const d = sumarDias(desde, n); setDesde(d); setHasta(sumarDias(hasta, n)); };
  const semana = () => { setDesde(hoy()); setHasta(sumarDias(hoy(), 6)); };

  const facturables = turnos.filter(t => t.estado === "atendido" && !t.factura_id);
  const marcados = facturables.filter(t => sel[t.id]);
  const todosMarcados = facturables.length > 0 && marcados.length === facturables.length;
  const iniciarFactura = (lista) => {
    if (!lista.length) return;
    if (new Set(lista.map(t => t.cliente_id || "")).size > 1) { toast.error("Los turnos elegidos son de clientes distintos: facturalos por separado"); return; }
    setFacturar(lista);
  };
  const facturaInicial = facturar?.length ? {
    empresa_id: facturar[0].cliente_id, _empresa_id: facturar[0].cliente_id,
    conceptos: facturar.map(t => {
      const m = Number(t.monto_cobrado ?? t.monto ?? 0);
      return {
        descripcion: `${t.servicio_nombre} — ${t.paciente_nombre} (${fmtFecha(t.inicio)})`,
        cantidad: 1, precio_unitario: m, precio_costo: 0, subtotal: m, producto_id: t.producto_id || "", iva_tipo: "10",
      };
    }),
  } : null;

  return (
    <div>
      <div className={`${CARD} p-3 mb-4 flex flex-wrap gap-3 items-end`}>
        <div className="flex items-end gap-1">
          <button className={BTN_ICO} onClick={() => dia(-1)}><ChevronLeft className="w-4 h-4" /></button>
          <div><label className={LABEL}>Desde</label><DateInput value={desde} onChange={e => setDesde(e.target.value)} className={INPUT} /></div>
          <div><label className={LABEL}>Hasta</label><DateInput value={hasta} onChange={e => setHasta(e.target.value)} className={INPUT} /></div>
          <button className={BTN_ICO} onClick={() => dia(1)}><ChevronRight className="w-4 h-4" /></button>
        </div>
        <button className={BTN_SEC} onClick={() => { setDesde(hoy()); setHasta(hoy()); }}>Hoy</button>
        <button className={BTN_SEC} onClick={semana}>7 días</button>
        {!ctx?.profesional_id && <div className="min-w-[160px]"><label className={LABEL}>Profesional</label>
          <select className={INPUT} value={fProf} onChange={e => setFProf(e.target.value)}>
            <option value="">Todos</option>{profesionales.filter(p => p.activo !== false).map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select></div>}
        <div className="relative"><label className={LABEL}>Estado</label>
          <button type="button" className={`${INPUT} flex items-center justify-between gap-2 min-w-[150px]`} onClick={() => setEstadoAbierto(v => !v)}>
            <span>{fEstados.length === 0 ? "Todos" : fEstados.join(", ")}</span><ChevronDown className="w-4 h-4 text-slate-400" /></button>
          {estadoAbierto && (
            <div className="absolute z-20 mt-1 w-48 bg-arandu-dark-light border border-white/10 rounded-lg p-2 shadow-xl">
              <label className="flex items-center gap-2 px-2 py-1 text-sm text-slate-300 cursor-pointer"><input type="checkbox" checked={fEstados.length === 0} onChange={() => setFEstados([])} />Todos</label>
              {ESTADOS_LISTA.map(es => (
                <label key={es} className="flex items-center gap-2 px-2 py-1 text-sm text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={fEstados.includes(es)} onChange={e => setFEstados(prev => e.target.checked ? [...prev, es] : prev.filter(x => x !== es))} />{es}</label>))}
              <div className="text-right mt-1"><button className="text-xs text-teal-300" onClick={() => setEstadoAbierto(false)}>Cerrar</button></div>
            </div>)}</div>
        <div className="flex-1 min-w-[160px]"><label className={LABEL}>Buscar</label>
          <div className="relative"><Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" />
            <input className={`${INPUT} pl-9`} placeholder="Paciente, servicio…" value={q} onChange={e => setQ(e.target.value)} /></div></div>
        {P("clinica_agenda.crear") && <button className={BTN_PRI} onClick={() => setForm({ inicio_fecha: desde, inicio_hora: "08:00" })}><Plus className="w-4 h-4" />Nuevo turno</button>}
        {P("clinica_agenda.facturar") && marcados.length > 0 && <button className={`${BTN_PRI} !bg-violet-600 hover:!bg-violet-700`} onClick={() => iniciarFactura(marcados)}><Receipt className="w-4 h-4" />Facturar seleccionados ({marcados.length})</button>}
      </div>

      <div className={`${CARD} overflow-x-auto`}>
        <table className="w-full">
          <thead className="border-b border-white/10"><tr>
            <th className={`${TH} w-8`}>{P("clinica_agenda.facturar") && facturables.length > 0 &&
              <input type="checkbox" title="Seleccionar todos los atendidos sin facturar" checked={todosMarcados} onChange={e => setSel(e.target.checked ? Object.fromEntries(facturables.map(t => [t.id, true])) : {})} />}</th>
            <th className={TH}>Fecha</th><th className={TH}>Hora</th><th className={TH}>Paciente</th><th className={TH}>Servicio</th>
            <th className={TH}>Profesional</th><th className={TH}>Monto</th><th className={TH}>Estado</th><th className={TH}></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-6 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>}
            {!loading && turnos.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500 text-sm">Sin turnos en el período</td></tr>}
            {turnos.map(t => (
              <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className={TD}>{P("clinica_agenda.facturar") && t.estado === "atendido" && !t.factura_id &&
                  <input type="checkbox" checked={!!sel[t.id]} onChange={e => setSel({ ...sel, [t.id]: e.target.checked })} />}</td>
                <td className={TD}>{fmtFecha(t.inicio)}</td>
                <td className={TD}>{fmtHora(t.inicio)}–{fmtHora(t.fin)}</td>
                <td className={TD}><div>{t.paciente_nombre}</div>{t.seguro_nombre && <div className="text-xs text-slate-500">{t.seguro_nombre}</div>}</td>
                <td className={TD}>{t.servicio_nombre}</td>
                <td className={TD}>{t.profesional_nombre}</td>
                <td className={TD}>{fmtMonto(t.monto_cobrado ?? t.monto, t.moneda)}
                  {t.estado === "atendido" && <div className="text-xs text-teal-400">Com. {fmtMonto(t.comision_monto, t.moneda)}</div>}</td>
                <td className={TD}><Badge estado={t.estado} />
                  {t.factura_numero && <div className="text-xs text-slate-500 mt-1">Fact. {t.factura_numero}</div>}
                  {t.liquidacion_id && <div className="text-xs text-slate-500">Liquidado</div>}
                  {t.asistencia && <div className={`text-xs mt-1 ${t.asistencia.verificada ? "text-emerald-400" : "text-amber-400"}`}><Fingerprint className="w-3 h-3 inline mr-1" />
                    {t.asistencia.verificada ? "Asistió (huella verificada)" : t.asistencia.metodo === "huella" ? "Asistió (huella sin verificar)" : "Asistió (manual)"}</div>}</td>
                <td className={`${TD} whitespace-nowrap`}>
                  <div className="flex gap-1 justify-end">
                    {P("clinica_agenda.editar") && t.estado === "pendiente" && <button className={BTN_ICO} title="Confirmar" onClick={() => cambiarEstado(t, "confirmado")}><Check className="w-4 h-4 text-sky-400" /></button>}
                    {P("clinica_agenda.atender") && ["pendiente", "confirmado"].includes(t.estado) && <button className={BTN_ICO} title="Marcar atendido" onClick={() => setAtender({ t, monto: t.monto })}><Stethoscope className="w-4 h-4 text-emerald-400" /></button>}
                    {P("clinica_agenda.asistencia") && ["pendiente", "confirmado", "atendido"].includes(t.estado) && <button className={BTN_ICO} title="Registrar asistencia (huella)" onClick={() => setAsistir(t)}><Fingerprint className={`w-4 h-4 ${t.asistencia ? "text-emerald-400" : "text-sky-300"}`} /></button>}
                    {P("clinica_agenda.facturar") && t.estado === "atendido" && !t.factura_id && <button className={BTN_ICO} title="Facturar" onClick={() => iniciarFactura([t])}><Receipt className="w-4 h-4 text-violet-400" /></button>}
                    {P("clinica_agenda.editar") && ["pendiente", "confirmado"].includes(t.estado) && <button className={BTN_ICO} title="Ausente" onClick={() => cambiarEstado(t, "ausente")}><UserX className="w-4 h-4" /></button>}
                    {P("clinica_agenda.cancelar") && ["pendiente", "confirmado"].includes(t.estado) && <button className={BTN_ICO} title="Cancelar" onClick={() => { if (window.confirm("¿Cancelar el turno?")) cambiarEstado(t, "cancelado"); }}><Ban className="w-4 h-4 text-red-400" /></button>}
                    {P("clinica_agenda.editar") && !t.liquidacion_id && !t.factura_id && <button className={BTN_ICO} title="Editar / reprogramar" onClick={() => setForm({ ...t, inicio_fecha: t.inicio.slice(0, 10), inicio_hora: t.inicio.slice(11, 16) })}><Pencil className="w-4 h-4" /></button>}
                    {P("clinica_agenda.editar") && t.estado === "atendido" && !t.liquidacion_id && !t.factura_id && <button className={BTN_ICO} title="Reabrir (volver a pendiente)" onClick={() => cambiarEstado(t, "pendiente")}><RefreshCw className="w-4 h-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {asistir && <AsistenciaModal turno={asistir} api={api} P={P} logo={logo} onClose={() => setAsistir(null)} onSaved={() => { setAsistir(null); cargar(); }} />}
      {form && <TurnoForm ctx={ctx} form={form} onClose={() => setForm(null)} api={api} profesionales={profesionales} servicios={servicios} pacientes={pacientes}
        onSaved={() => { setForm(null); cargar(); }} />}

      {atender && (
        <Modal titulo="Marcar turno atendido" onClose={() => setAtender(null)} ancho="max-w-md">
          <p className="text-sm text-slate-300 mb-3">{atender.t.paciente_nombre} — {atender.t.servicio_nombre}</p>
          <label className={LABEL}>Monto cobrado al cliente ({atender.t.moneda || "PYG"})</label>
          <input className={INPUT} type="number" min="0" step="any" value={atender.monto ?? ""} onChange={e => setAtender({ ...atender, monto: e.target.value })} />
          <p className="text-xs text-slate-500 mt-2">La comisión del profesional se calcula con este monto según su configuración.</p>
          <div className="flex justify-end gap-2 mt-4">
            <button className={BTN_SEC} onClick={() => setAtender(null)}>Cancelar</button>
            <button className={BTN_PRI} onClick={async () => { await cambiarEstado(atender.t, "atendido", { monto_cobrado: Number(atender.monto || 0) }); setAtender(null); }}><Check className="w-4 h-4" />Confirmar</button>
          </div>
        </Modal>
      )}

      {facturar && (
        <FacturaFormModal
          factura={null} sinFactura={false} token={token} API={API} empresas={clientes} presupuestosDisp={[]}
          activeEmpresaPropia={activeEmpresaPropia} hasPermission={hasPermission} productos={[]} productosHabilitados={false}
          cuentasDisp={cuentas} planCuentasDisp={planCuentas} initialValues={facturaInicial} defaultEmpresaId={facturar[0].cliente_id || ""}
          onClose={() => setFacturar(null)}
          onSaved={async (saved) => {
            const lista = facturar; setFacturar(null);
            if (saved?.id) {
              try { await api("/admin/clinica/turnos/vincular-factura-lote", { method: "POST", body: { turno_ids: lista.map(t => t.id), factura_id: saved.id, factura_numero: saved.numero || "" } }); }
              catch (e) { toast.error(`Factura creada, pero no se vinculó a los turnos: ${e.message}`); }
            }
            cargar();
          }}
        />
      )}
    </div>
  );
}

function TurnoForm({ ctx, form, onClose, api, profesionales, servicios, pacientes, onSaved }) {
  const [f, setF] = useState({
    paciente_id: form.paciente_id || "", profesional_id: ctx?.profesional_id || form.profesional_id || "", servicio_id: form.servicio_id || "",
    inicio_fecha: form.inicio_fecha || hoy(), inicio_hora: form.inicio_hora || "08:00",
    duracion_min: form.duracion_min || "", monto: form.monto ?? "", notas: form.notas || "",
  });
  const [saving, setSaving] = useState(false);
  const [busq, setBusq] = useState("");
  const editando = !!form.id;
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const pacs = pacientes.filter(p => p.activo !== false && (!busq || (p.nombre || "").toLowerCase().includes(busq.toLowerCase())));

  const onServicio = (id) => {
    const s = servicios.find(x => x.id === id);
    setF(p => ({ ...p, servicio_id: id, duracion_min: s?.duracion_min || p.duracion_min, monto: editando ? p.monto : (s?.precio ?? p.monto) }));
  };

  const guardar = async (forzar = false) => {
    setSaving(true);
    try {
      const body = {
        paciente_id: f.paciente_id, profesional_id: f.profesional_id, servicio_id: f.servicio_id,
        inicio: `${f.inicio_fecha}T${f.inicio_hora}`, duracion_min: f.duracion_min ? Number(f.duracion_min) : undefined,
        monto: f.monto === "" ? undefined : Number(f.monto), notas: f.notas, forzar,
      };
      await api(editando ? `/admin/clinica/turnos/${form.id}` : "/admin/clinica/turnos", { method: editando ? "PUT" : "POST", body });
      toast.success(editando ? "Turno actualizado" : "Turno agendado");
      onSaved();
    } catch (e) {
      if (/ya tiene un turno/.test(e.message) && window.confirm(`${e.message}\n\n¿Agendar igual (sobreturno)?`)) { setSaving(false); return guardar(true); }
      toast.error(e.message);
    }
    setSaving(false);
  };

  return (
    <Modal titulo={editando ? "Editar turno" : "Nuevo turno"} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className={LABEL}>Paciente *</label>
          <input className={`${INPUT} mb-1`} placeholder="Filtrar paciente…" value={busq} onChange={e => setBusq(e.target.value)} />
          <select className={INPUT} value={f.paciente_id} onChange={e => set("paciente_id", e.target.value)}>
            <option value="">— Elegir —</option>{pacs.map(p => <option key={p.id} value={p.id}>{p.nombre}{p.ruc ? ` (${p.ruc})` : ""}</option>)}
          </select>
          <p className="text-xs text-slate-500 mt-1">¿Paciente nuevo? Crealo en la pestaña Pacientes.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={LABEL}>Profesional *</label>
            <select className={INPUT} value={f.profesional_id} disabled={!!ctx?.profesional_id} onChange={e => set("profesional_id", e.target.value)}>
              <option value="">— Elegir —</option>{profesionales.filter(p => p.activo !== false).map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select></div>
          <div><label className={LABEL}>Servicio *</label>
            <select className={INPUT} value={f.servicio_id} onChange={e => onServicio(e.target.value)}>
              <option value="">— Elegir —</option>{servicios.filter(s => s.activo !== false).map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className={LABEL}>Fecha *</label><DateInput value={f.inicio_fecha} onChange={e => set("inicio_fecha", e.target.value)} className={INPUT} /></div>
          <div><label className={LABEL}>Hora *</label><input type="time" className={INPUT} value={f.inicio_hora} onChange={e => set("inicio_hora", e.target.value)} /></div>
          <div><label className={LABEL}>Duración (min)</label><input type="number" min="5" step="5" className={INPUT} value={f.duracion_min} onChange={e => set("duracion_min", e.target.value)} /></div>
        </div>
        <div><label className={LABEL}>Monto del servicio</label><input type="number" min="0" step="any" className={INPUT} value={f.monto} onChange={e => set("monto", e.target.value)} /></div>
        <div><label className={LABEL}>Notas</label><textarea rows={2} className={INPUT} value={f.notas} onChange={e => set("notas", e.target.value)} /></div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button className={BTN_SEC} onClick={onClose}>Cancelar</button>
        <button className={BTN_PRI} disabled={saving} onClick={() => guardar(false)}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Guardar</button>
      </div>
    </Modal>
  );
}

/* ═════════════════════ ASISTENCIA (huella del Catastro) ═════════════════════ */

function AsistenciaModal({ turno, api, P, logo, onClose, onSaved }) {
  const [ref, setRef] = useState(null);       // huella registrada del paciente en el Catastro
  const [cfg, setCfg] = useState(null);       // lector configurado para la empresa
  const [cap, setCap] = useState(null);       // { imagen, template, score, verificada }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api(`/admin/clinica/pacientes/${turno.paciente_id}/huella`).then(setRef).catch(e => { toast.error(e.message); setRef({ tiene_huella: false }); });
    api("/admin/config-huella", { query: logo ? { logo_tipo: logo } : {} }).then(setCfg).catch(() => setCfg(null));
    // eslint-disable-next-line
  }, []);

  const hayLector = !!cfg && !!cfg.url;
  const guardar = async (body) => {
    setBusy(true);
    try { await api(`/admin/clinica/turnos/${turno.id}/asistencia`, { method: "POST", body }); toast.success("Asistencia registrada"); onSaved(); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const capturar = async () => {
    setBusy(true);
    try {
      const r = await capturarDesdeServicioLocal(cfg);
      const score = ref?.template ? await compararSecuGen(cfg, r.template, ref.template) : null;
      setCap({ ...r, score, verificada: score != null && score >= UMBRAL_COINCIDENCIA_SECUGEN });
    } catch (e) {
      toast.error(e.message?.startsWith("El lector") ? e.message : `No se pudo conectar con el servicio local de ${cfg?.dispositivo_nombre || "el lector"}. Iniciá el servicio y conectá el lector USB.`);
    }
    setBusy(false);
  };

  return (
    <Modal titulo="Registrar asistencia" onClose={onClose} ancho="max-w-lg">
      <p className="text-sm text-slate-300 mb-3">{turno.paciente_nombre} — {turno.servicio_nombre} · {fmtFecha(turno.inicio)} {fmtHora(turno.inicio)}</p>
      {ref == null && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
      {ref && !ref.tiene_huella && <p className="text-xs text-amber-400 mb-3">Este paciente no tiene huella registrada en el Catastro (cliente → Huella digital). Podés capturarla igual como constancia de asistencia.</p>}
      {ref?.tiene_huella && !ref.template && <p className="text-xs text-slate-400 mb-3">La huella del Catastro es una imagen sin plantilla: no se puede comparar automáticamente, se guarda la captura como constancia.</p>}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="text-center"><div className="text-xs text-slate-400 mb-1">Registrada (Catastro)</div>
          <div className="h-36 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden">{ref?.imagen ? <img src={ref.imagen} alt="huella registrada" className="max-h-36 bg-white p-1 rounded" /> : <span className="text-xs text-slate-500">—</span>}</div></div>
        <div className="text-center"><div className="text-xs text-slate-400 mb-1">Capturada hoy</div>
          <div className="h-36 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden">{cap?.imagen ? <img src={cap.imagen} alt="huella capturada" className="max-h-36 bg-white p-1 rounded" /> : <span className="text-xs text-slate-500">—</span>}</div></div>
      </div>
      {cap && <div className={`text-sm mb-3 ${cap.verificada ? "text-emerald-400" : "text-amber-400"}`}>
        {cap.score != null ? (cap.verificada ? `Coincide con la huella registrada (puntaje ${cap.score}).` : `No coincide (puntaje ${cap.score}, mínimo ${UMBRAL_COINCIDENCIA_SECUGEN}). Probá de nuevo.`) : "Huella capturada (sin comparación automática): verificá visualmente."}</div>}
      <div className="flex flex-wrap justify-end gap-2 mt-4">
        <button className={BTN_SEC} onClick={onClose}>Cancelar</button>
        {hayLector && <button className={BTN_SEC} disabled={busy} onClick={capturar}><Fingerprint className="w-4 h-4" />{cap ? "Volver a capturar" : "Capturar huella"}</button>}
        {cap && <button className={BTN_PRI} disabled={busy} onClick={() => guardar({ metodo: "huella", verificada: cap.verificada, score: cap.score, imagen: cap.imagen })}><Check className="w-4 h-4" />Registrar asistencia</button>}
        {P("clinica_agenda.editar") && <button className={BTN_SEC} disabled={busy} onClick={() => guardar({ metodo: "manual", nota: "Registro manual (sin huella)" })}>Registrar sin huella</button>}
      </div>
      {!hayLector && <p className="text-xs text-slate-500 mt-3">Esta empresa no tiene lector configurado (Catastro → Configuración → Lector de huella).</p>}
    </Modal>
  );
}

/* ═══════════════════════════════ PACIENTES ══════════════════════════════ */

function Pacientes({ ctx, api, P, pacientes, profesionales, clientes, token, logo, recargar, recargarClientes, activeEmpresaPropia }) {
  const [q, setQ] = useState("");
  const [nuevo, setNuevo] = useState(false);
  const [ficha, setFicha] = useState(null);
  const lista = pacientes.filter(p => !q || [p.nombre, p.ruc, p.nro_afiliado, p.telefono].some(v => (v || "").toLowerCase().includes(q.toLowerCase())));
  return (
    <div>
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" />
          <input className={`${INPUT} pl-9`} placeholder="Buscar por nombre, RUC/CI, afiliado o teléfono…" value={q} onChange={e => setQ(e.target.value)} /></div>
        {P("clinica_pacientes.crear") && <button className={BTN_PRI} onClick={() => setNuevo(true)}><Plus className="w-4 h-4" />Nuevo paciente</button>}
      </div>
      <div className={`${CARD} overflow-x-auto`}>
        <table className="w-full">
          <thead className="border-b border-white/10"><tr><th className={TH}>Paciente</th><th className={TH}>RUC / CI</th><th className={TH}>Teléfono</th><th className={TH}>Edad</th><th className={TH}>Seguro</th><th className={TH}></th></tr></thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500 text-sm">Sin pacientes</td></tr>}
            {lista.map(p => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => setFicha(p)}>
                <td className={TD}>{p.nombre}{p.activo === false && <span className="ml-2 text-xs text-slate-500">(inactivo)</span>}</td>
                <td className={TD}>{p.ruc}</td><td className={TD}>{p.telefono}</td><td className={TD}>{p.edad ?? ""}</td>
                <td className={TD}>{p.seguro_nombre}{p.nro_afiliado ? ` · ${p.nro_afiliado}` : ""}</td>
                <td className={TD}><FileText className="w-4 h-4 text-slate-500" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {nuevo && <NuevoPaciente api={api} clientes={clientes} pacientes={pacientes} token={token} logo={logo} recargarClientes={recargarClientes}
        onClose={() => setNuevo(false)} onSaved={(p) => { setNuevo(false); recargar(); if (p) setFicha(p); }} />}
      {ficha && <FichaPaciente pacienteId={ficha.id} api={api} P={P} token={token} ctx={ctx} profesionales={profesionales} empresa={activeEmpresaPropia} onClose={() => setFicha(null)} recargar={recargar} />}
    </div>
  );
}

function NuevoPaciente({ api, clientes, pacientes, token, logo, recargarClientes, onClose, onSaved }) {
  const [cliente, setCliente] = useState(null);
  const [f, setF] = useState({ fecha_nacimiento: "", peso: "", ocupacion: "", seguro_nombre: "", nro_afiliado: "", estado_civil: "", contacto_emergencia: "", telefono_paciente: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  // Al elegir el cliente se trae su fecha de nacimiento del Catastro (editable)
  useEffect(() => {
    const fn = cliente?.fecha_nacimiento;
    if (fn) setF(p => ({ ...p, fecha_nacimiento: String(fn).slice(0, 10) }));
  }, [cliente]);
  const yaPaciente = cliente && pacientes.some(p => p.cliente_id === cliente.id);
  const guardar = async () => {
    if (!cliente?.id) return toast.error("Elegí o creá el cliente");
    setSaving(true);
    try {
      const p = await api("/admin/clinica/pacientes", { method: "POST", body: { cliente_id: cliente.id, ...f, peso: f.peso === "" ? null : Number(f.peso), fecha_nacimiento: f.fecha_nacimiento || null } });
      toast.success("Paciente registrado"); onSaved(p);
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };
  return (
    <Modal titulo="Nuevo paciente" onClose={onClose}>
      <label className={LABEL}>Cliente (Catastro) — buscá o creá uno nuevo sin salir del módulo</label>
      <ClienteSearchSelect empresas={clientes} value={cliente} onSelect={setCliente} onCreated={recargarClientes} token={token} logo={logo} />
      {yaPaciente && <p className="text-xs text-amber-400 mt-1">Ese cliente ya está registrado como paciente.</p>}
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div><label className={LABEL}>Fecha de nacimiento</label><DateInput value={f.fecha_nacimiento} onChange={e => set("fecha_nacimiento", e.target.value)} className={INPUT} /></div>
        <div><label className={LABEL}>Peso (kg)</label><input type="number" step="any" className={INPUT} value={f.peso} onChange={e => set("peso", e.target.value)} /></div>
        <div><label className={LABEL}>Seguro / obra social</label><input className={INPUT} value={f.seguro_nombre} onChange={e => set("seguro_nombre", e.target.value)} /></div>
        <div><label className={LABEL}>N° de afiliado</label><input className={INPUT} value={f.nro_afiliado} onChange={e => set("nro_afiliado", e.target.value)} /></div>
        <div><label className={LABEL}>Ocupación</label><input className={INPUT} value={f.ocupacion} onChange={e => set("ocupacion", e.target.value)} /></div>
        <div><label className={LABEL}>Estado civil</label><input className={INPUT} value={f.estado_civil} onChange={e => set("estado_civil", e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div><label className={LABEL}>Contacto de emergencia</label><input className={INPUT} value={f.contacto_emergencia} onChange={e => set("contacto_emergencia", e.target.value)} /></div>
        <div><label className={LABEL}>Telf. Paciente</label><input className={INPUT} value={f.telefono_paciente} onChange={e => set("telefono_paciente", e.target.value)} placeholder="Teléfono del paciente en sí" /></div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button className={BTN_SEC} onClick={onClose}>Cancelar</button>
        <button className={BTN_PRI} disabled={saving || yaPaciente} onClick={guardar}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Registrar</button>
      </div>
    </Modal>
  );
}

function FichaPaciente({ pacienteId, api, P, token, ctx, profesionales, empresa, onClose, recargar }) {
  const [p, setP] = useState(null);
  const [exp, setExp] = useState(null);
  const [aba, setAba] = useState("ficha");
  const [f, setF] = useState({});
  const [ind, setInd] = useState({ diagnostico: "", ejercicios: "", instrucciones: "", profesional_id: ctx?.profesional_id || "" });
  const [evo, setEvo] = useState("");
  const [tipoEst, setTipoEst] = useState("Radiografía");
  const puedeVerExp = P("clinica_expediente.ver");

  const cargar = useCallback(async () => {
    try {
      const pac = await api(`/admin/clinica/pacientes/${pacienteId}`);
      setP(pac); setF({ ...pac });
      if (puedeVerExp) setExp(await api(`/admin/clinica/pacientes/${pacienteId}/expediente`));
    } catch (e) { toast.error(e.message); }
  }, [api, pacienteId, puedeVerExp]);
  useEffect(() => { cargar(); }, [cargar]);

  const guardarFicha = async () => {
    try {
      await api(`/admin/clinica/pacientes/${pacienteId}`, { method: "PUT", body: {
        fecha_nacimiento: f.fecha_nacimiento || null, peso: f.peso === "" || f.peso == null ? null : Number(f.peso), ocupacion: f.ocupacion, seguro_nombre: f.seguro_nombre,
        nro_afiliado: f.nro_afiliado, estado_civil: f.estado_civil, contacto_emergencia: f.contacto_emergencia, antecedentes: f.antecedentes, estado_actual: f.estado_actual,
        telefono_paciente: f.telefono_paciente ?? "",
      } });
      toast.success("Ficha guardada"); recargar(); onClose();
    } catch (e) { toast.error(e.message); }
  };
  const post = async (path, body, ok) => { try { await api(path, { method: "POST", body }); toast.success(ok); cargar(); return true; } catch (e) { toast.error(e.message); return false; } };
  const borrar = async (tipo, id) => {
    if (!window.confirm("¿Eliminar este registro del expediente?")) return;
    try { await api(`/admin/clinica/expediente/${tipo}/${id}`, { method: "DELETE" }); cargar(); } catch (e) { toast.error(e.message); }
  };
  const subir = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("tipo_estudio", tipoEst);
    try {
      const r = await fetch(`${API}/admin/clinica/pacientes/${pacienteId}/estudios?${new URLSearchParams({ logo_tipo: p.logo_tipo })}`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "No se pudo subir");
      toast.success("Archivo subido"); cargar();
    } catch (e) { toast.error(e.message); }
  };
  const descargar = async (e) => {
    try {
      const r = await fetch(`${API}/admin/clinica/estudios/${e.id}/archivo?${new URLSearchParams({ logo_tipo: p.logo_tipo })}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("No se pudo descargar");
      const blob = await r.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = e.nombre_original || "estudio"; a.click(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const imprimirDoc = async (tipo, indicacion) => {
    const prid = indicacion.profesional_id || ctx?.profesional_id || ind.profesional_id || "";
    let firmante = {};
    if (prid) { try { firmante = await api(`/admin/clinica/profesionales/${prid}/firma`); } catch { firmante = {}; } }
    else toast.info("La indicación no tiene profesional asignado: se imprime sin firma");
    if (!imprimirDocumentoClinico({ tipo, empresa, paciente: p, indicacion, firmante })) toast.error("El navegador bloqueó la ventana de impresión: permití ventanas emergentes");
  };

  if (!p) return <Modal titulo="Paciente" onClose={onClose} ancho="max-w-3xl"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></Modal>;
  const ABAS = [["ficha", "Ficha"], ...(puedeVerExp ? [["indicaciones", "Indicaciones"], ["evoluciones", "Evoluciones"], ["estudios", "Estudios"]] : []), ["turnos", "Turnos"]];
  const puedeEditar = P("clinica_pacientes.editar");
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  return (
    <Modal titulo={`${p.nombre}${p.edad != null ? ` · ${p.edad} años` : ""}`} onClose={onClose} ancho="max-w-3xl">
      <div className="text-xs text-slate-400 mb-3">{[p.ruc && `RUC/CI ${p.ruc}`, p.telefono, p.email].filter(Boolean).join(" · ")}</div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {ABAS.map(([k, l]) => <button key={k} onClick={() => setAba(k)} className={`px-3 py-1.5 text-sm rounded-lg border ${aba === k ? "bg-teal-500/20 border-teal-500/40 text-teal-300" : "border-white/10 text-slate-400"}`}>{l}</button>)}
      </div>

      {aba === "ficha" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={LABEL}>Fecha de nacimiento</label><DateInput value={f.fecha_nacimiento || ""} onChange={e => set("fecha_nacimiento", e.target.value)} className={INPUT} readOnly={!puedeEditar} /></div>
            <div><label className={LABEL}>Peso (kg)</label><input type="number" step="any" className={INPUT} value={f.peso ?? ""} onChange={e => set("peso", e.target.value)} disabled={!puedeEditar} /></div>
            <div><label className={LABEL}>Seguro</label><input className={INPUT} value={f.seguro_nombre || ""} onChange={e => set("seguro_nombre", e.target.value)} disabled={!puedeEditar} /></div>
            <div><label className={LABEL}>N° afiliado</label><input className={INPUT} value={f.nro_afiliado || ""} onChange={e => set("nro_afiliado", e.target.value)} disabled={!puedeEditar} /></div>
            <div><label className={LABEL}>Ocupación</label><input className={INPUT} value={f.ocupacion || ""} onChange={e => set("ocupacion", e.target.value)} disabled={!puedeEditar} /></div>
            <div><label className={LABEL}>Estado civil</label><input className={INPUT} value={f.estado_civil || ""} onChange={e => set("estado_civil", e.target.value)} disabled={!puedeEditar} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={LABEL}>Contacto de emergencia</label><input className={INPUT} value={f.contacto_emergencia || ""} onChange={e => set("contacto_emergencia", e.target.value)} disabled={!puedeEditar} /></div>
            <div><label className={LABEL}>Telf. Paciente</label><input className={INPUT} value={f.telefono_paciente || ""} onChange={e => set("telefono_paciente", e.target.value)} disabled={!puedeEditar} placeholder={p.telefono_catastro ? `Catastro: ${p.telefono_catastro}` : "Teléfono del paciente en sí"} /></div>
          </div>
          {puedeVerExp && <>
            <div><label className={LABEL}>Antecedentes</label><textarea rows={3} className={INPUT} value={f.antecedentes || ""} onChange={e => set("antecedentes", e.target.value)} disabled={!puedeEditar} /></div>
            <div><label className={LABEL}>Estado actual</label><textarea rows={3} className={INPUT} value={f.estado_actual || ""} onChange={e => set("estado_actual", e.target.value)} disabled={!puedeEditar} /></div>
          </>}
          {puedeEditar && <div className="flex justify-end"><button className={BTN_PRI} onClick={guardarFicha}><Save className="w-4 h-4" />Guardar ficha</button></div>}
        </div>
      )}

      {aba === "indicaciones" && exp && (
        <div className="space-y-3">
          {P("clinica_expediente.escribir") && (
            <div className="bg-white/5 rounded-xl p-3 space-y-2">
              <input className={INPUT} placeholder="Diagnóstico *" value={ind.diagnostico} onChange={e => setInd({ ...ind, diagnostico: e.target.value })} />
              <textarea rows={2} className={INPUT} placeholder="Ejercicios" value={ind.ejercicios} onChange={e => setInd({ ...ind, ejercicios: e.target.value })} />
              <textarea rows={2} className={INPUT} placeholder="Instrucciones" value={ind.instrucciones} onChange={e => setInd({ ...ind, instrucciones: e.target.value })} />
              <div className="flex flex-wrap items-center justify-end gap-2">
                <select className={`${INPUT} !w-64`} value={ind.profesional_id || ""} disabled={!!ctx?.profesional_id} onChange={e => setInd({ ...ind, profesional_id: e.target.value })} title="Profesional que firma certificado e indicaciones">
                  <option value="">Firma: elegir profesional…</option>{(profesionales || []).filter(x => x.activo !== false).map(x => <option key={x.id} value={x.id}>{x.nombre}</option>)}</select>
                <button className={BTN_PRI} onClick={async () => { if (await post(`/admin/clinica/pacientes/${pacienteId}/indicaciones`, { ...ind, profesional_id: ind.profesional_id || null }, "Indicación agregada")) setInd({ diagnostico: "", ejercicios: "", instrucciones: "", profesional_id: ctx?.profesional_id || ind.profesional_id || "" }); }}><Plus className="w-4 h-4" />Agregar</button></div>
            </div>
          )}
          {exp.indicaciones.length === 0 && <p className="text-sm text-slate-500">Sin indicaciones</p>}
          {exp.indicaciones.map(i => (
            <div key={i.id} className="border border-white/10 rounded-xl p-3 text-sm">
              <div className="flex justify-between"><b className="text-white">{i.diagnostico}</b>
                <span className="text-xs text-slate-500">{fmtFecha(i.fecha)} · {i.autor}{P("clinica_expediente.eliminar") && <button className="ml-2 text-red-400" onClick={() => borrar("indicaciones", i.id)}><Trash2 className="w-3.5 h-3.5 inline" /></button>}</span></div>
              {i.ejercicios && <p className="text-slate-300 mt-1 whitespace-pre-wrap"><span className="text-slate-500">Ejercicios: </span>{i.ejercicios}</p>}
              {i.instrucciones && <p className="text-slate-300 mt-1 whitespace-pre-wrap"><span className="text-slate-500">Instrucciones: </span>{i.instrucciones}</p>}
              {P("clinica_expediente.certificados") && (
                <div className="flex gap-2 mt-2">
                  <button className={BTN_SEC} onClick={() => imprimirDoc("certificado", i)}><Printer className="w-4 h-4" />Certificado</button>
                  <button className={BTN_SEC} onClick={() => imprimirDoc("indicaciones", i)}><Printer className="w-4 h-4" />Receta / Indicaciones</button>
                </div>)}
            </div>
          ))}
        </div>
      )}

      {aba === "evoluciones" && exp && (
        <div className="space-y-3">
          {P("clinica_expediente.escribir") && (
            <div className="bg-white/5 rounded-xl p-3 space-y-2">
              <textarea rows={3} className={INPUT} placeholder="Relato de la evolución…" value={evo} onChange={e => setEvo(e.target.value)} />
              <div className="flex justify-end"><button className={BTN_PRI} onClick={async () => { if (await post(`/admin/clinica/pacientes/${pacienteId}/evoluciones`, { relato: evo }, "Evolución agregada")) setEvo(""); }}><Plus className="w-4 h-4" />Agregar</button></div>
            </div>
          )}
          {exp.evoluciones.length === 0 && <p className="text-sm text-slate-500">Sin evoluciones</p>}
          {exp.evoluciones.map(e => (
            <div key={e.id} className="border border-white/10 rounded-xl p-3 text-sm">
              <div className="flex justify-between text-xs text-slate-500"><span>{fmtFecha(e.fecha)} {fmtHora(e.fecha)} · {e.autor}</span>
                {P("clinica_expediente.eliminar") && <button className="text-red-400" onClick={() => borrar("evoluciones", e.id)}><Trash2 className="w-3.5 h-3.5" /></button>}</div>
              <p className="text-slate-200 mt-1 whitespace-pre-wrap">{e.relato}</p>
            </div>
          ))}
        </div>
      )}

      {aba === "estudios" && exp && (
        <div className="space-y-3">
          {P("clinica_expediente.archivos") && (
            <div className="bg-white/5 rounded-xl p-3 flex gap-2 items-end flex-wrap">
              <div><label className={LABEL}>Tipo</label>
                <select className={INPUT} value={tipoEst} onChange={e => setTipoEst(e.target.value)}>
                  {["Radiografía", "Resonancia", "Tomografía", "Ecografía", "Laboratorio", "Informe médico", "Otro"].map(t => <option key={t}>{t}</option>)}
                </select></div>
              <label className={`${BTN_PRI} cursor-pointer`}><Plus className="w-4 h-4" />Subir archivo
                <input type="file" className="hidden" onChange={e => { subir(e.target.files?.[0]); e.target.value = ""; }} /></label>
            </div>
          )}
          {exp.estudios.length === 0 && <p className="text-sm text-slate-500">Sin estudios</p>}
          {exp.estudios.map(e => (
            <div key={e.id} className="border border-white/10 rounded-xl p-3 text-sm flex items-center gap-3">
              <div className="flex-1"><div className="text-white">{e.nombre_original}</div><div className="text-xs text-slate-500">{e.tipo_estudio} · {fmtFecha(e.fecha)} · {e.autor}</div></div>
              <button className={BTN_ICO} onClick={() => descargar(e)} title="Descargar"><Download className="w-4 h-4" /></button>
              {P("clinica_expediente.eliminar") && <button className={BTN_ICO} onClick={() => borrar("estudios", e.id)}><Trash2 className="w-4 h-4 text-red-400" /></button>}
            </div>
          ))}
        </div>
      )}

      {aba === "turnos" && (
        <TurnosPaciente api={api} pacienteId={pacienteId} exp={exp} />
      )}
    </Modal>
  );
}

function TurnosPaciente({ api, pacienteId, exp }) {
  const [t, setT] = useState(exp?.turnos || null);
  useEffect(() => { if (!exp) api("/admin/clinica/turnos", { query: { paciente_id: pacienteId } }).then(setT).catch(() => setT([])); }, [api, exp, pacienteId]);
  const lista = exp?.turnos || t || [];
  if (!lista.length) return <p className="text-sm text-slate-500">Sin turnos</p>;
  return (
    <table className="w-full"><thead className="border-b border-white/10"><tr><th className={TH}>Fecha</th><th className={TH}>Servicio</th><th className={TH}>Profesional</th><th className={TH}>Estado</th></tr></thead>
      <tbody>{lista.map(x => <tr key={x.id} className="border-b border-white/5"><td className={TD}>{fmtFecha(x.inicio)} {fmtHora(x.inicio)}</td><td className={TD}>{x.servicio_nombre}</td><td className={TD}>{x.profesional_nombre}</td><td className={TD}><Badge estado={x.estado} /></td></tr>)}</tbody></table>
  );
}

/* ═════════════════════════════ LIQUIDACIONES ════════════════════════════ */

function Liquidaciones({ api, P, profesionales, cuentas, recargar }) {
  const [profId, setProfId] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [pend, setPend] = useState([]);
  const [sel, setSel] = useState({});
  const [liqs, setLiqs] = useState([]);
  const [pagar, setPagar] = useState(null);

  const cargarLiqs = useCallback(async () => { try { setLiqs(await api("/admin/clinica/liquidaciones")); } catch (e) { toast.error(e.message); } }, [api]);
  const cargarPend = useCallback(async () => {
    if (!profId) { setPend([]); return; }
    try {
      const query = { profesional_id: profId }; if (desde) query.desde = desde; if (hasta) query.hasta = hasta;
      const d = await api("/admin/clinica/liquidaciones/pendientes", { query });
      setPend(d); setSel(Object.fromEntries(d.map(t => [t.id, true])));
    } catch (e) { toast.error(e.message); }
  }, [api, profId, desde, hasta]);
  useEffect(() => { cargarLiqs(); }, [cargarLiqs]);
  useEffect(() => { cargarPend(); }, [cargarPend]);

  const ids = pend.filter(t => sel[t.id]).map(t => t.id);
  const total = pend.filter(t => sel[t.id]).reduce((s, t) => s + Number(t.comision_monto || 0), 0);
  const mon = pend[0]?.moneda || "PYG";

  const generar = async () => {
    try {
      await api("/admin/clinica/liquidaciones", { method: "POST", body: { profesional_id: profId, turno_ids: ids } });
      toast.success("Liquidación generada"); cargarPend(); cargarLiqs();
    } catch (e) { toast.error(e.message); }
  };
  const anular = async (l) => {
    if (!window.confirm(`¿Anular ${l.numero}?${l.estado === "pagada" ? " Se revertirá el egreso de la cuenta." : ""}`)) return;
    try { await api(`/admin/clinica/liquidaciones/${l.id}/anular`, { method: "POST" }); toast.success("Liquidación anulada"); cargarLiqs(); cargarPend(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5">
      {P("clinica_liquidaciones.generar") && (
        <div className={`${CARD} p-4`}>
          <h3 className="text-sm font-semibold text-white mb-3">Comisiones pendientes de liquidar</h3>
          <div className="flex flex-wrap gap-3 items-end mb-3">
            <div className="min-w-[200px]"><label className={LABEL}>Profesional</label>
              <select className={INPUT} value={profId} onChange={e => setProfId(e.target.value)}><option value="">— Elegir —</option>{profesionales.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></div>
            <div><label className={LABEL}>Desde</label><DateInput value={desde} onChange={e => setDesde(e.target.value)} className={INPUT} /></div>
            <div><label className={LABEL}>Hasta</label><DateInput value={hasta} onChange={e => setHasta(e.target.value)} className={INPUT} /></div>
          </div>
          {profId && (pend.length === 0 ? <p className="text-sm text-slate-500">Sin turnos atendidos pendientes de liquidar.</p> : <>
            <table className="w-full"><thead className="border-b border-white/10"><tr><th className={TH}></th><th className={TH}>Fecha</th><th className={TH}>Paciente</th><th className={TH}>Servicio</th><th className={TH}>Cobrado</th><th className={TH}>Comisión</th></tr></thead>
              <tbody>{pend.map(t => (
                <tr key={t.id} className="border-b border-white/5">
                  <td className={TD}><input type="checkbox" checked={!!sel[t.id]} onChange={e => setSel({ ...sel, [t.id]: e.target.checked })} /></td>
                  <td className={TD}>{fmtFecha(t.inicio)}</td><td className={TD}>{t.paciente_nombre}</td><td className={TD}>{t.servicio_nombre}</td>
                  <td className={TD}>{fmtMonto(t.monto_cobrado, t.moneda)}</td>
                  <td className={TD}>{fmtMonto(t.comision_monto, t.moneda)} <span className="text-xs text-slate-500">({t.comision_tipo === "pct" ? `${t.comision_valor}%` : "fijo"})</span></td>
                </tr>))}</tbody></table>
            <div className="flex justify-between items-center mt-3">
              <div className="text-sm text-slate-300">{ids.length} turnos · Total comisión: <b className="text-teal-300">{fmtMonto(total, mon)}</b></div>
              <button className={BTN_PRI} disabled={!ids.length || total <= 0} onClick={generar}><Wallet className="w-4 h-4" />Generar liquidación</button>
            </div></>)}
        </div>
      )}

      <div className={`${CARD} overflow-x-auto`}>
        <div className="p-4 pb-2 text-sm font-semibold text-white">Liquidaciones emitidas</div>
        <table className="w-full"><thead className="border-b border-white/10"><tr><th className={TH}>N°</th><th className={TH}>Profesional</th><th className={TH}>Período</th><th className={TH}>Turnos</th><th className={TH}>Monto</th><th className={TH}>Estado</th><th className={TH}></th></tr></thead>
          <tbody>
            {liqs.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-sm">Sin liquidaciones</td></tr>}
            {liqs.map(l => (
              <tr key={l.id} className="border-b border-white/5">
                <td className={TD}>{l.numero}</td><td className={TD}>{l.profesional_nombre}</td>
                <td className={TD}>{fmtFecha(l.periodo_desde)} – {fmtFecha(l.periodo_hasta)}</td><td className={TD}>{l.cantidad_turnos}</td>
                <td className={TD}>{fmtMonto(l.monto, l.moneda)}</td>
                <td className={TD}><span className={`text-xs px-2 py-0.5 rounded-full ${l.estado === "pagada" ? "bg-emerald-500/15 text-emerald-400" : l.estado === "anulada" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>{l.estado}</span>
                  {l.fecha_pago && <div className="text-xs text-slate-500">{fmtFecha(l.fecha_pago)} · {l.cuenta_nombre}</div>}</td>
                <td className={`${TD} text-right whitespace-nowrap`}>
                  {l.estado === "pendiente" && P("clinica_liquidaciones.pagar") && <button className={BTN_SEC} onClick={() => setPagar({ l, cuenta_id: "", fecha_pago: hoy() })}><Wallet className="w-4 h-4" />Pagar</button>}
                  {l.estado !== "anulada" && P("clinica_liquidaciones.anular") && <button className={`${BTN_ICO} ml-1`} title="Anular" onClick={() => anular(l)}><Ban className="w-4 h-4 text-red-400" /></button>}
                </td>
              </tr>))}
          </tbody></table>
      </div>

      {pagar && (
        <Modal titulo={`Pagar ${pagar.l.numero}`} onClose={() => setPagar(null)} ancho="max-w-md">
          <p className="text-sm text-slate-300 mb-3">{pagar.l.profesional_nombre} — <b>{fmtMonto(pagar.l.monto, pagar.l.moneda)}</b></p>
          <label className={LABEL}>Cuenta / caja de salida</label>
          <select className={INPUT} value={pagar.cuenta_id} onChange={e => setPagar({ ...pagar, cuenta_id: e.target.value })}>
            <option value="">Cuenta predeterminada</option>
            {cuentas.filter(c => c.activa !== false && (c.moneda || "PYG") === (pagar.l.moneda || "PYG")).map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.moneda})</option>)}
          </select>
          <label className={`${LABEL} mt-3`}>Fecha de pago</label>
          <DateInput value={pagar.fecha_pago} onChange={e => setPagar({ ...pagar, fecha_pago: e.target.value })} className={INPUT} />
          <div className="flex justify-end gap-2 mt-5">
            <button className={BTN_SEC} onClick={() => setPagar(null)}>Cancelar</button>
            <button className={BTN_PRI} onClick={async () => {
              try { await api(`/admin/clinica/liquidaciones/${pagar.l.id}/pagar`, { method: "POST", body: { cuenta_id: pagar.cuenta_id || undefined, fecha_pago: pagar.fecha_pago } }); toast.success("Pago registrado"); setPagar(null); cargarLiqs(); }
              catch (e) { toast.error(e.message); }
            }}><Check className="w-4 h-4" />Registrar pago</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═══════════════════════════════ REPORTES ═══════════════════════════════ */

function Reportes({ ctx, api, profesionales }) {
  const d0 = hoy().slice(0, 8) + "01";
  const [desde, setDesde] = useState(d0);
  const [hasta, setHasta] = useState(hoy());
  const [prof, setProf] = useState("");
  const [r, setR] = useState(null);
  const cargar = useCallback(async () => {
    try { setR(await api("/admin/clinica/reportes/produccion", { query: { desde, hasta, ...(prof ? { profesional_id: prof } : {}) } })); } catch (e) { toast.error(e.message); }
  }, [api, desde, hasta, prof]);
  useEffect(() => { cargar(); }, [cargar]);
  const Tabla = ({ titulo, filas }) => (
    <div className={`${CARD} overflow-x-auto`}>
      <div className="p-3 pb-1 text-sm font-semibold text-white">{titulo}</div>
      <table className="w-full"><thead className="border-b border-white/10"><tr><th className={TH}>Nombre</th><th className={TH}>Turnos</th><th className={TH}>Cobrado</th><th className={TH}>Comisión</th></tr></thead>
        <tbody>{filas.length === 0 ? <tr><td colSpan={4} className="p-4 text-center text-slate-500 text-sm">Sin datos</td></tr> :
          filas.map(f => <tr key={f.nombre} className="border-b border-white/5"><td className={TD}>{f.nombre}</td><td className={TD}>{f.turnos}</td><td className={TD}>{fmtMonto(f.cobrado)}</td><td className={TD}>{fmtMonto(f.comision)}</td></tr>)}</tbody></table>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div><label className={LABEL}>Desde</label><DateInput value={desde} onChange={e => setDesde(e.target.value)} className={INPUT} /></div>
        <div><label className={LABEL}>Hasta</label><DateInput value={hasta} onChange={e => setHasta(e.target.value)} className={INPUT} /></div>
        {!ctx?.profesional_id && <div className="min-w-[180px]"><label className={LABEL}>Profesional</label>
          <select className={INPUT} value={prof} onChange={e => setProf(e.target.value)}><option value="">Todos</option>{(profesionales || []).map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></div>}
        {ctx?.profesional_id && <span className="text-xs text-teal-300 pb-2">Mostrando solo tu producción</span>}
      </div>
      {r && <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["Turnos", r.total_turnos], ["Atendidos", r.total_atendidos], ["Cobrado", fmtMonto(r.total_cobrado)], ["Comisiones", fmtMonto(r.total_comision)]].map(([l, v]) => (
            <div key={l} className={`${CARD} p-4`}><div className="text-xs text-slate-400">{l}</div><div className="text-xl font-bold text-white mt-1">{v}</div></div>))}
        </div>
        <div className="text-xs text-slate-400">Por estado: {Object.entries(r.estados).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"} · Montos mostrados en ₲ (turnos en otra moneda se suman sin convertir).</div>

        <div className="text-sm font-semibold text-white pt-2">Detalle de turnos atendidos por profesional</div>
        {(r.detalle_por_profesional || []).length === 0 && <p className="text-sm text-slate-500">Sin turnos atendidos en el período.</p>}
        {(r.detalle_por_profesional || []).map(d => (
          <div key={d.nombre} className={`${CARD} overflow-x-auto`}>
            <div className="p-3 pb-1 flex justify-between text-sm"><b className="text-white">{d.nombre}</b>
              <span className="text-slate-400">{d.turnos.length} turnos · Cobrado <b className="text-white">{fmtMonto(d.cobrado)}</b> · Comisión <b className="text-teal-300">{fmtMonto(d.comision)}</b></span></div>
            <table className="w-full"><thead className="border-b border-white/10"><tr>
              <th className={TH}>Fecha</th><th className={TH}>Hora</th><th className={TH}>Paciente</th><th className={TH}>Servicio</th><th className={TH}>Seguro</th>
              <th className={`${TH} text-right`}>Precio</th><th className={`${TH} text-right`}>Cobrado</th><th className={`${TH} text-right`}>Comisión</th><th className={TH}>Factura</th><th className={TH}>Asist.</th></tr></thead>
              <tbody>{d.turnos.map(t => (
                <tr key={t.id} className="border-b border-white/5">
                  <td className={TD}>{fmtFecha(t.fecha)}</td><td className={TD}>{t.hora}</td><td className={TD}>{t.paciente}</td><td className={TD}>{t.servicio}</td><td className={TD}>{t.seguro}</td>
                  <td className={`${TD} text-right`}>{fmtMonto(t.precio, t.moneda)}</td><td className={`${TD} text-right`}>{fmtMonto(t.cobrado, t.moneda)}</td>
                  <td className={`${TD} text-right`}>{fmtMonto(t.comision, t.moneda)}<div className="text-xs text-slate-500">{t.comision_tipo === "pct" ? `${t.comision_valor}%` : t.comision_tipo === "monto" ? "fijo" : ""}</div></td>
                  <td className={TD}>{t.factura || <span className="text-slate-500">—</span>}{t.liquidado && <div className="text-xs text-slate-500">liquidado</div>}</td>
                  <td className={TD}>{t.asistencia ? <Fingerprint className={`w-4 h-4 ${t.asistencia_verificada ? "text-emerald-400" : "text-amber-400"}`} title={t.asistencia_verificada ? "Huella verificada" : "Registrada"} /> : <span className="text-slate-600">—</span>}</td>
                </tr>))}</tbody></table>
          </div>))}

        <Tabla titulo="Por profesional" filas={r.por_profesional} />
        <Tabla titulo="Por servicio" filas={r.por_servicio} />
        <Tabla titulo="Por seguro / particular" filas={r.por_seguro} />
      </>}
    </div>
  );
}

/* ═════════════════════════════ CONFIGURACIÓN ════════════════════════════ */

function leerFirma(file, cb) {
  // Reduce la imagen a ≤ 600 px de ancho (conserva transparencia en PNG) antes de guardarla
  const fr = new FileReader();
  fr.onload = () => {
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, 600 / img.width);
      const c = document.createElement("canvas"); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      let out = c.toDataURL("image/png");
      if (out.length > 900000) out = c.toDataURL("image/jpeg", 0.85);
      cb(out);
    };
    img.src = fr.result;
  };
  fr.readAsDataURL(file);
}

function CfgProfesionales({ ctx, api, P, profesionales, recargar }) {
  const [form, setForm] = useState(null);
  const [empleados, setEmpleados] = useState([]);
  useEffect(() => { api("/admin/entidades", { query: { es_empleado: "true", activo: "true" } }).then(setEmpleados).catch(() => setEmpleados([])); }, [api]);
  const puede = P("clinica_configuracion.profesionales");
  const guardar = async () => {
    try {
      const body = { nombre: form.nombre, telefono: form.telefono, especialidad: form.especialidad, empleado_id: form.empleado_id || null,
        usuario_id: form.usuario_id || "", titulo: form.titulo || "", matricula: form.matricula || "" };
      if (form.firma_nueva) body.firma = form.firma_nueva;
      else if (form.quitar_firma) body.firma = "";
      if (form.id) await api(`/admin/clinica/profesionales/${form.id}`, { method: "PUT", body });
      else await api("/admin/clinica/profesionales", { method: "POST", body });
      toast.success("Profesional guardado"); setForm(null); recargar();
    } catch (e) { toast.error(e.message); }
  };
  const toggle = async (p) => { try { await api(`/admin/clinica/profesionales/${p.id}`, { method: "PUT", body: { activo: p.activo === false } }); recargar(); } catch (e) { toast.error(e.message); } };
  return (
    <div>
      {puede && <div className="mb-3"><button className={BTN_PRI} onClick={() => setForm({ nombre: "", telefono: "", especialidad: "", empleado_id: "" })}><Plus className="w-4 h-4" />Nuevo profesional</button></div>}
      <div className={`${CARD} overflow-x-auto`}><table className="w-full">
        <thead className="border-b border-white/10"><tr><th className={TH}>Nombre</th><th className={TH}>Especialidad</th><th className={TH}>Teléfono</th><th className={TH}>Usuario</th><th className={TH}>Estado</th><th className={TH}></th></tr></thead>
        <tbody>{profesionales.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500 text-sm">Sin profesionales</td></tr>}
          {profesionales.map(p => (
            <tr key={p.id} className="border-b border-white/5"><td className={TD}>{p.nombre}{p.tiene_firma && <span className="ml-2 text-xs text-emerald-400">firma ✓</span>}</td><td className={TD}>{p.especialidad}</td><td className={TD}>{p.telefono}</td>
              <td className={TD}>{(ctx?.usuarios || []).find(u => u.id === p.usuario_id)?.name || (p.usuario_id ? "vinculado" : <span className="text-slate-600">—</span>)}</td>
              <td className={TD}>{p.activo === false ? <span className="text-slate-500">Inactivo</span> : <span className="text-emerald-400">Activo</span>}</td>
              <td className={`${TD} text-right`}>{puede && <><button className={BTN_ICO} onClick={() => setForm({ ...p })}><Pencil className="w-4 h-4" /></button>
                <button className={`${BTN_ICO} ml-1`} onClick={() => toggle(p)} title={p.activo === false ? "Activar" : "Desactivar"}><Ban className="w-4 h-4" /></button></>}</td></tr>))}
        </tbody></table></div>
      {form && (
        <Modal titulo={form.id ? "Editar profesional" : "Nuevo profesional"} onClose={() => setForm(null)} ancho="max-w-md">
          <div className="space-y-3">
            {!form.id && <div><label className={LABEL}>Tomar de empleado del Catastro (opcional)</label>
              <select className={INPUT} value={form.empleado_id || ""} onChange={e => { const emp = empleados.find(x => x.id === e.target.value); setForm({ ...form, empleado_id: e.target.value, nombre: emp?.nombre || form.nombre, telefono: emp?.telefono || form.telefono }); }}>
                <option value="">— Ninguno —</option>{empleados.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}</select></div>}
            <div><label className={LABEL}>Nombre *</label><input className={INPUT} value={form.nombre || ""} onChange={e => setForm({ ...form, nombre: e.target.value })} /></div>
            <div><label className={LABEL}>Especialidad</label><input className={INPUT} value={form.especialidad || ""} onChange={e => setForm({ ...form, especialidad: e.target.value })} /></div>
            <div><label className={LABEL}>Teléfono</label><input className={INPUT} value={form.telefono || ""} onChange={e => setForm({ ...form, telefono: e.target.value })} /></div>
            <div><label className={LABEL}>Título / credenciales (aparece en certificado e indicaciones)</label><input className={INPUT} value={form.titulo || ""} onChange={e => setForm({ ...form, titulo: e.target.value })} placeholder="Lic. Kinesiología y Fisiatría - Especialista en…" /></div>
            <div><label className={LABEL}>Matrícula</label><input className={INPUT} value={form.matricula || ""} onChange={e => setForm({ ...form, matricula: e.target.value })} /></div>
            <div><label className={LABEL}>Usuario del sistema (si lo tiene, ese usuario solo verá sus propios turnos y producción)</label>
              <select className={INPUT} value={form.usuario_id || ""} onChange={e => setForm({ ...form, usuario_id: e.target.value })}>
                <option value="">— Sin usuario —</option>{(ctx?.usuarios || []).map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}</select></div>
            <div><label className={LABEL}>Firma (imagen PNG/JPG, idealmente fondo blanco o transparente)</label>
              <div className="flex items-center gap-3 flex-wrap">
                <label className={`${BTN_SEC} cursor-pointer`}><Upload className="w-4 h-4" />{form.firma_nueva || form.tiene_firma ? "Cambiar firma" : "Subir firma"}
                  <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={e => { const fl = e.target.files?.[0]; e.target.value = ""; if (fl) leerFirma(fl, d => setForm(prev => ({ ...prev, firma_nueva: d, quitar_firma: false }))); }} /></label>
                {form.firma_nueva && <img src={form.firma_nueva} alt="firma" className="h-12 bg-white rounded p-1" />}
                {!form.firma_nueva && form.tiene_firma && !form.quitar_firma && <span className="text-xs text-emerald-400">Firma cargada</span>}
                {(form.firma_nueva || (form.tiene_firma && !form.quitar_firma)) && <button className="text-xs text-red-400" onClick={() => setForm({ ...form, firma_nueva: null, quitar_firma: true })}>Quitar</button>}
              </div></div>
          </div>
          <div className="flex justify-end gap-2 mt-5"><button className={BTN_SEC} onClick={() => setForm(null)}>Cancelar</button><button className={BTN_PRI} onClick={guardar}><Save className="w-4 h-4" />Guardar</button></div>
        </Modal>)}
    </div>
  );
}

function CfgServicios({ api, P, servicios, recargar }) {
  const [form, setForm] = useState(null);
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const puede = P("clinica_configuracion.servicios");
  useEffect(() => {
    if (!form || form.id) return;
    const t = setTimeout(() => { api("/admin/clinica/catalogo-productos", { query: { q } }).then(setRes).catch(() => setRes([])); }, 250);
    return () => clearTimeout(t);
  }, [api, form, q]);
  const guardar = async () => {
    try {
      if (form.id) await api(`/admin/clinica/servicios/${form.id}`, { method: "PUT", body: { nombre: form.nombre, duracion_min: Number(form.duracion_min || 30) } });
      else await api("/admin/clinica/servicios", { method: "POST", body: { producto_id: form.producto_id, nombre: form.nombre, duracion_min: Number(form.duracion_min || 30) } });
      toast.success("Servicio guardado"); setForm(null); recargar();
    } catch (e) { toast.error(e.message); }
  };
  const toggle = async (s) => { try { await api(`/admin/clinica/servicios/${s.id}`, { method: "PUT", body: { activo: s.activo === false } }); recargar(); } catch (e) { toast.error(e.message); } };
  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">Los servicios son productos del Catálogo: el precio se toma de allí. Creá el producto/servicio en Productos y acá lo habilitás para la agenda.</p>
      {puede && <div className="mb-3"><button className={BTN_PRI} onClick={() => { setQ(""); setForm({ producto_id: "", nombre: "", duracion_min: 30 }); }}><Plus className="w-4 h-4" />Agregar servicio</button></div>}
      <div className={`${CARD} overflow-x-auto`}><table className="w-full">
        <thead className="border-b border-white/10"><tr><th className={TH}>Servicio</th><th className={TH}>SKU</th><th className={TH}>Precio</th><th className={TH}>Duración</th><th className={TH}>Estado</th><th className={TH}></th></tr></thead>
        <tbody>{servicios.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500 text-sm">Sin servicios</td></tr>}
          {servicios.map(s => (
            <tr key={s.id} className="border-b border-white/5"><td className={TD}>{s.nombre}</td><td className={TD}>{s.producto_sku}</td><td className={TD}>{fmtMonto(s.precio, s.moneda)}</td><td className={TD}>{s.duracion_min} min</td>
              <td className={TD}>{s.activo === false ? <span className="text-slate-500">Inactivo</span> : <span className="text-emerald-400">Activo</span>}</td>
              <td className={`${TD} text-right`}>{puede && <><button className={BTN_ICO} onClick={() => setForm({ ...s })}><Pencil className="w-4 h-4" /></button>
                <button className={`${BTN_ICO} ml-1`} onClick={() => toggle(s)}><Ban className="w-4 h-4" /></button></>}</td></tr>))}
        </tbody></table></div>
      {form && (
        <Modal titulo={form.id ? "Editar servicio" : "Agregar servicio"} onClose={() => setForm(null)} ancho="max-w-md">
          {!form.id && <div className="mb-3"><label className={LABEL}>Producto del catálogo</label>
            <input className={`${INPUT} mb-1`} placeholder="Buscar por nombre o SKU…" value={q} onChange={e => setQ(e.target.value)} />
            <div className="max-h-40 overflow-y-auto border border-white/10 rounded-lg">
              {res.map(p => <div key={p.id} onClick={() => setForm({ ...form, producto_id: p.id, nombre: p.nombre })}
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-white/10 ${form.producto_id === p.id ? "bg-teal-500/20 text-teal-300" : "text-slate-300"}`}>{p.nombre} <span className="text-xs text-slate-500">{p.sku}</span></div>)}
              {res.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">Sin resultados</div>}
            </div></div>}
          <div className="space-y-3">
            <div><label className={LABEL}>Nombre en agenda</label><input className={INPUT} value={form.nombre || ""} onChange={e => setForm({ ...form, nombre: e.target.value })} /></div>
            <div><label className={LABEL}>Duración (minutos)</label><input type="number" min="5" step="5" className={INPUT} value={form.duracion_min || ""} onChange={e => setForm({ ...form, duracion_min: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5"><button className={BTN_SEC} onClick={() => setForm(null)}>Cancelar</button><button className={BTN_PRI} disabled={!form.id && !form.producto_id} onClick={guardar}><Save className="w-4 h-4" />Guardar</button></div>
        </Modal>)}
    </div>
  );
}

function CfgComisiones({ api, P, profesionales, servicios, comisiones, recargar }) {
  const puede = P("clinica_configuracion.comisiones");
  const [sel, setSel] = useState("");
  const [edit, setEdit] = useState({});   // clave servicio_id|"" -> {tipo, valor}
  const reglas = comisiones.filter(c => c.profesional_id === sel);
  const general = reglas.find(r => !r.servicio_id);
  useEffect(() => {
    const m = {};
    reglas.forEach(r => { m[r.servicio_id || ""] = { tipo: r.tipo, valor: r.valor }; });
    setEdit(m);
    // eslint-disable-next-line
  }, [sel, comisiones]);

  const guardar = async (servicio_id) => {
    const e = edit[servicio_id || ""];
    if (!e || e.valor === "" || e.valor == null) return toast.error("Indicá el valor");
    try { await api("/admin/clinica/comisiones", { method: "PUT", body: { profesional_id: sel, servicio_id: servicio_id || null, tipo: e.tipo || "pct", valor: Number(e.valor) } }); toast.success("Comisión guardada"); recargar(); }
    catch (er) { toast.error(er.message); }
  };
  const borrar = async (servicio_id) => {
    const r = reglas.find(x => (x.servicio_id || "") === (servicio_id || ""));
    if (!r) return;
    try { await api(`/admin/clinica/comisiones/${r.id}`, { method: "DELETE" }); toast.success("Regla eliminada"); recargar(); } catch (er) { toast.error(er.message); }
  };
  const Fila = ({ titulo, sid, sub }) => {
    const e = edit[sid || ""] || { tipo: "pct", valor: "" };
    const existe = reglas.some(x => (x.servicio_id || "") === (sid || ""));
    return (
      <tr className="border-b border-white/5">
        <td className={TD}>{titulo}{sub && <div className="text-xs text-slate-500">{sub}</div>}</td>
        <td className={TD}><select disabled={!puede} className={`${INPUT} !w-40`} value={e.tipo || "pct"} onChange={ev => setEdit({ ...edit, [sid || ""]: { ...e, tipo: ev.target.value } })}>
          <option value="pct">% de lo cobrado</option><option value="monto">Monto fijo por turno</option></select></td>
        <td className={TD}><input disabled={!puede} type="number" min="0" step="any" className={`${INPUT} !w-28`} value={e.valor ?? ""} onChange={ev => setEdit({ ...edit, [sid || ""]: { ...e, valor: ev.target.value } })} /></td>
        <td className={`${TD} text-right whitespace-nowrap`}>{puede && <>
          <button className={BTN_ICO} onClick={() => guardar(sid)} title="Guardar"><Save className="w-4 h-4 text-teal-300" /></button>
          {existe && <button className={`${BTN_ICO} ml-1`} onClick={() => borrar(sid)} title="Quitar regla"><Trash2 className="w-4 h-4 text-red-400" /></button>}</>}</td>
      </tr>);
  };
  return (
    <div>
      <p className="text-xs text-slate-500 mb-3">Cada profesional tiene una comisión general (% de lo cobrado al cliente, o un monto fijo por turno). Opcionalmente podés fijar una excepción por servicio; la excepción tiene prioridad sobre la general.</p>
      <div className="max-w-xs mb-4"><label className={LABEL}>Profesional</label>
        <select className={INPUT} value={sel} onChange={e => setSel(e.target.value)}><option value="">— Elegir —</option>{profesionales.filter(p => p.activo !== false).map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select></div>
      {sel && (
        <div className={`${CARD} overflow-x-auto`}><table className="w-full">
          <thead className="border-b border-white/10"><tr><th className={TH}>Aplica a</th><th className={TH}>Tipo</th><th className={TH}>Valor</th><th className={TH}></th></tr></thead>
          <tbody>
            <Fila titulo="Comisión general" sub={general ? "" : "Sin definir (0%)"} sid="" />
            {servicios.filter(s => s.activo !== false).map(s => <Fila key={s.id} titulo={s.nombre} sub="Excepción por servicio" sid={s.id} />)}
          </tbody></table></div>)}
    </div>
  );
}

function CfgAdministradores({ ctx, api, recargarCtx }) {
  const [sel, setSel] = useState(ctx?.admins || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setSel(ctx?.admins || []); }, [ctx?.admins]);
  const guardar = async () => {
    setSaving(true);
    try { await api("/admin/clinica/config", { method: "PUT", body: { admins: sel } }); toast.success("Administradores guardados"); recargarCtx(); }
    catch (e) { toast.error(e.message); }
    setSaving(false);
  };
  return (
    <div className="max-w-2xl">
      <p className="text-xs text-slate-400 mb-3">
        El administrador del módulo ve y gestiona los turnos, la producción y las comisiones de <b>todos</b> los profesionales de la clínica, sin ser administrador del sistema.
        Los usuarios vinculados a un profesional (Configuración → Profesionales) que no estén marcados acá solo ven sus propios turnos y su producción. Los administradores y gerentes del sistema siempre tienen acceso total.
      </p>
      <div className={`${CARD} divide-y divide-white/5`}>
        {(ctx?.usuarios || []).length === 0 && <p className="p-4 text-sm text-slate-500">No hay usuarios asignados a esta empresa.</p>}
        {(ctx?.usuarios || []).map(u => (
          <label key={u.id} className="flex items-center gap-3 px-4 py-2 text-sm text-slate-200 cursor-pointer">
            <input type="checkbox" checked={sel.includes(u.id)} onChange={e => setSel(prev => e.target.checked ? [...prev, u.id] : prev.filter(x => x !== u.id))} />
            <span>{u.name}</span><span className="text-xs text-slate-500">{u.email}</span>
            {u.role === "gerente" && <span className="text-xs text-teal-300 ml-auto">gerente (siempre administra)</span>}
          </label>))}
      </div>
      <div className="flex justify-end mt-4"><button className={BTN_PRI} disabled={saving} onClick={guardar}><Save className="w-4 h-4" />Guardar</button></div>
    </div>
  );
}
